import { makeStyles } from "@material-ui/core/styles";
import { lazy, Suspense, useEffect } from "react";
import { Route, Switch } from "react-router";
import { RouteComponentProps } from "react-router-dom";
import ScrollMemory from "react-router-scroll-memory";
import { changeAppState, useAppState } from "../../common/appState";
import { reloadAndRedirect_OneTimeReload } from "../../common/functions";
import { useScreenSize } from "../../common/hooks/useScreenSize";
import { useStateLifecycle } from "../../common/hooks/useStateLifecycle";
import { useUser } from "../../common/hooks/useUser";
import { APP_VERSION } from "../../version";
import { ReturnToLocalMenu } from "../LocalDebug/App";
import ShurikenProgress from "../shared/Animations/ShurikenProgress";
import Helmet from "../shared/Helmet";
import { ReMountMain } from "../shared/Remount";
import Layout from "./Layout";

const Home = lazy(() => import("./zApps/Home"));
const LessonList = lazy(() => import("./zApps/Student/LessonList"));
const Lesson = lazy(() => import("./zApps/Student/Lesson"));
const BatchStudents = lazy(() => import("./zApps/Teacher/BatchStudents"));
const FlashcardList = lazy(() => import("./zApps/Flashcard"));
const ResetPassword = lazy(() => import("./zApps/Admin/ResetPassword"));
const NotFound = lazy(() => import("../shared/404"));
const LogForAdmin = lazy(() => import("./zApps/Admin/LogForAdmin"));
const CourseEditTop = lazy(() => import("./zApps/Admin/CourseEditTop"));
const CourseEdit = lazy(() => import("./zApps/Admin/CourseEdit"));
const LessonEdit = lazy(() => import("./zApps/Admin/LessonEdit"));
const TeacherList = lazy(() => import("./zApps/Admin/TeacherList"));
const DeletedUserList = lazy(() => import("./zApps/Teacher/DeletedUserList"));
const EditHowToUse = lazy(() => import("./zApps/Admin/EditHowToUse"));
const JTestAdmin = lazy(() => import("./zApps/Admin/JTestAdmin"));

export const darkModeClassNameAndKey = "LnDarkMode";
export function App() {
    useStateLifecycle();

    const { user, isUserFetchDone } = useUser();
    useEffect(() => {
        if (location.href.includes("/flashcard")) {
            return; // Flashcardは画面遷移させずにログインを促すので、飛ばさない
        }
        if (isUserFetchDone && !user && location.pathname != "/") {
            // ログインしていないユーザーはトップページへ飛ばす
            location.href = "/";
        }
    }, [user, isUserFetchDone]);

    const [isDarkMode] = useAppState("isDarkMode");
    useEffect(() => {
        if (isDarkMode == null) {
            changeAppState(
                "isDarkMode",
                localStorage.getItem(darkModeClassNameAndKey) === "true"
            );
            return;
        }
        const html = document.getElementsByTagName("html")[0];
        if (isDarkMode) {
            html.classList.add(darkModeClassNameAndKey);

            // PC版FirefoxでYouTube動画を全画面表示したときに色が反転する問題の対策
            const onFullScreenChange = () => {
                const html = document.documentElement;
                if (document.fullscreenElement) {
                    html.classList.remove(darkModeClassNameAndKey); // 全画面に入ったとき
                } else {
                    html.classList.add(darkModeClassNameAndKey); // 全画面を出たとき
                }
            };

            document.addEventListener("fullscreenchange", onFullScreenChange);
            return () => {
                document.removeEventListener(
                    "fullscreenchange",
                    onFullScreenChange
                );
            };
        }
        html.classList.remove(darkModeClassNameAndKey);
    }, [isDarkMode]);

    return (
        <Layout>
            <ReMountMain>
                <Suspense fallback={<LoadingAnimation />}>
                    <ScrollMemory />
                    <Switch>
                        <Route sensitive exact path="/" component={Home} />
                        <Route
                            sensitive
                            exact
                            path="/lessons/:courseId"
                            component={LessonList}
                        />
                        <Route
                            sensitive
                            exact
                            path="/lesson/:lessonId"
                            component={Lesson}
                        />
                        <Route
                            sensitive
                            exact
                            path="/batch-students/:batchId"
                            component={BatchStudents}
                        />
                        <Route
                            sensitive
                            path="/u/:userId"
                            component={({
                                match: {
                                    params: { userId },
                                },
                            }: RouteComponentProps<{ userId?: string }>) => {
                                window.location.href = `/#u${userId}`;
                                return null;
                            }}
                        />
                        <Route
                            sensitive
                            exact
                            path="/flashcard"
                            component={FlashcardList}
                        />
                        <Route
                            sensitive
                            exact
                            path="/flashcard/:setId"
                            component={FlashcardList}
                        />
                        <Route
                            sensitive
                            exact
                            path="/reset_password/:code"
                            component={ResetPassword}
                        />
                        <Route
                            sensitive
                            path="/system-log/:targetUserId"
                            component={LogForAdmin}
                        />
                        <Route
                            sensitive
                            path="/edit-course-top"
                            component={CourseEditTop}
                        />
                        <Route
                            sensitive
                            path="/edit-course/:courseId"
                            component={CourseEdit}
                        />
                        <Route
                            sensitive
                            path="/edit-lesson/:lessonId"
                            component={LessonEdit}
                        />
                        <Route
                            sensitive
                            path="/teacher-list"
                            component={TeacherList}
                        />
                        <Route
                            sensitive
                            path="/deleted-users"
                            component={DeletedUserList}
                        />
                        <Route
                            sensitive
                            path="/jtest-admin"
                            component={JTestAdmin}
                        />
                        <Route
                            sensitive
                            path="/help"
                            component={EditHowToUse}
                        />
                        <Route
                            sensitive
                            path="/not-found"
                            component={NotFound}
                        />
                        <Route
                            sensitive
                            exact
                            path="/local"
                            component={ReturnToLocalMenu}
                        />
                        <Route component={NotFoundRedirect} />
                    </Switch>
                </Suspense>
            </ReMountMain>
        </Layout>
    );
}

function NotFoundRedirect() {
    const url = `api/SystemBase/GetVersion/V${new Date().getMilliseconds()}`;
    fetch(url).then(res => {
        res.json().then(v => {
            if (Number(v) !== APP_VERSION) {
                //@ts-ignore
                window.location.reload(true);
            } else {
                reloadAndRedirect_OneTimeReload("pageNotFoundRedirect");
            }
        });
    });

    return (
        <>
            <Helmet noindex />
            <LoadingAnimation />
        </>
    );
}

export function LoadingAnimation() {
    const { screenWidth, screenHeight } = useScreenSize();
    const c = useLoadingAnimationStyles();
    return (
        <div className={c.container}>
            <ShurikenProgress size={Math.min(screenWidth, screenHeight) / 5} />
        </div>
    );
}
const useLoadingAnimationStyles = makeStyles({
    container: {
        width: "100%",
        height: "100%",
        position: "fixed",
        top: 0,
        left: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
});
